import { useAuthContext } from '@healthblocks-io/core'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSetRecoilState } from 'recoil'

import { useAuthenticedSession } from '@/modules/auth/hooks/useAuthenticatedSession'
import { useSessionCredentials } from '@/modules/auth/hooks/useSessionCredentials'
import { useBiometricsPermission } from '@/modules/biometrics/hooks/useBiometricsPermission'
import { useCanUseBiometrics } from '@/modules/biometrics/hooks/useCanUseBiometrics'
import { BiometricsAppPermission } from '@/modules/biometrics/state/biometrics.atom'
import { useOAuth2 } from '@/modules/healthblocks/hooks/useOAuth2'
import { elapsedTimeAtom } from '@/modules/idle-timer/state/idle-timer.atoms'
import { useLockoutTime } from '@/modules/shared/hooks/useLockoutTime'
import { useTenantHref } from '@/modules/shared/hooks/useTenantHref'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'

export type LoginState =
  | 'idle'
  | 'authenticating'
  | 'checking_biometrics_permission'
  | 'verifying_biometrics'
  | 'authenticated'

export const useLoginFlow = () => {
  const [state, setState] = useState<LoginState>('idle')

  // Hooks
  const navigate = useNavigate()
  const tenantHref = useTenantHref()
  const auth = useAuthContext()
  const isAuthenticated = !!auth.data.current
  const setLastInteraction = useSetRecoilState(elapsedTimeAtom)
  const { canUseBiometrics } = useCanUseBiometrics()
  const { biometricPermission, updateBiometricsPermission } = useBiometricsPermission()
  const { saveCredentials, getCredentials } = useSessionCredentials()
  const { authenticate: startEHealthFlow, loading: eHealthLoading, isProcessingCallback } = useOAuth2()
  const { setLockout, lockout: isLocked } = useLockoutTime()
  const { updateLastLoggedIn, isSessionExpired } = useAuthenticedSession()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  // Methods
  const handleLogout = useCallback(async () => {
    auth.signOut()
    navigate(tenantHref('/login'))
    setState('idle')
  }, [auth, navigate, tenantHref])

  const handleLoginSuccess = useCallback(async () => {
    setLockout(false)
    // Reset idle timer on successful login to avoid instantly locking again
    // due to a persisted "last interaction" timestamp from a previous session.
    setLastInteraction(Date.now())
    updateLastLoggedIn()

    if (!isLoggedInForMandatee && auth.data.current) {
      // Store credentials for future biometric login
      await saveCredentials('user', auth.data.current.access_token ?? '', auth.data.current.refresh_token ?? '')

      // Check for biometrics permission prompt
      if (biometricPermission === BiometricsAppPermission.SKIPPED) {
        updateBiometricsPermission(BiometricsAppPermission.PROMPT)
      }
    }
  }, [
    auth.data,
    biometricPermission,
    isLoggedInForMandatee,
    saveCredentials,
    setLastInteraction,
    setLockout,
    updateBiometricsPermission,
    updateLastLoggedIn,
  ])

  const startLogin = useCallback(async () => {
    // Trigger eHealth login
    await startEHealthFlow()
  }, [startEHealthFlow])

  const handleLoginWithBiometrics = useCallback(async () => {
    if (!canUseBiometrics) return

    setState('verifying_biometrics')
    try {
      const credentials = await getCredentials()
      if (credentials) {
        // Re-authenticate using tokens stored in biometrics
        auth.setData(
          {
            access_token: credentials.accessToken,
            refresh_token: credentials.refreshToken,
          },
          true,
        )

        setLockout(false)
        setLastInteraction(Date.now())
        setState('authenticated')
      } else {
        // No credentials found despite permission?
        setState('idle')
      }
    } catch (error) {
      console.error('Biometric login failed', error)
      setState('idle')
    }
  }, [auth, canUseBiometrics, getCredentials, setLastInteraction, setLockout])

  // Effects
  useEffect(() => {
    if (isAuthenticated) {
      setState('authenticated')
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (isSessionExpired) {
      handleLogout()
    }
  }, [isSessionExpired, handleLogout])

  useEffect(() => {
    if (eHealthLoading) {
      setState('authenticating')
    } else if (state === 'authenticating') {
      if (isAuthenticated) {
        // Login success
        setState('authenticated')
        handleLoginSuccess()
      } else {
        // Login failed or cancelled
        setState('idle')
      }
    }
  }, [eHealthLoading, handleLoginSuccess, isAuthenticated, state])

  return {
    state,
    isLocked: isLoggedInForMandatee ? false : isLocked,
    canUseBiometrics,
    biometricPermission,
    startLogin,
    handleLoginWithBiometrics,
    handleLogout,
    updateBiometricsPermission,
    // Legacy/Compatibility aliases
    authenticate: startLogin,
    authenticatedAndLoading: state === 'authenticating',
    shouldBeLoading: state === 'authenticating', // simplified mapping
    // OAuth callback processing state
    isProcessingCallback,
    isSessionExpired,
  }
}
